/**
 * Kandidatenstatus — die einzige gültige Definition (Schritt 2).
 *
 * Führend ist `candidates.workflow_stage` (Enum `candidate_workflow_stage`,
 * Migration 042_02). Die Stufe beschreibt ausschließlich den
 * Bearbeitungszustand; Prozessinformationen (vorgestellt, Interview, Angebot,
 * vermittelt) liegen in `submissions`, Bewertung in `priority`.
 *
 * Abgelöst und nicht mehr zu verwenden: `stage`, `status`, `talent_status`,
 * `internal_classification`.
 */

export type CandidateStage =
  | 'NEW'
  | 'IN_PROGRESS'
  | 'CONTACT_ATTEMPTED'
  | 'CONTACTED'
  | 'QUALIFIED'
  | 'ACTIVE'
  | 'TALENT_POOL'
  | 'INACTIVE'
  | 'DO_NOT_CONTACT';

export type StageGroup = 'unbearbeitet' | 'in_arbeit' | 'ruhend';

interface StageInfo {
  label: string;
  kurz: string;
  color: string;
  bgColor: string;
  group: StageGroup;
}

export const CANDIDATE_STAGE_CONFIG: Record<CandidateStage, StageInfo> = {
  NEW:               { label: 'Neu',                kurz: 'Neu',        color: '#3B82F6', bgColor: 'rgba(59,130,246,0.12)',  group: 'unbearbeitet' },
  IN_PROGRESS:       { label: 'In Bearbeitung',     kurz: 'In Arbeit',  color: '#6366F1', bgColor: 'rgba(99,102,241,0.12)',  group: 'in_arbeit' },
  CONTACT_ATTEMPTED: { label: 'Kontaktversuch',     kurz: 'Versuch',    color: '#F59E0B', bgColor: 'rgba(245,158,11,0.12)',  group: 'in_arbeit' },
  CONTACTED:         { label: 'Gesprochen',         kurz: 'Gesprochen', color: '#8B5CF6', bgColor: 'rgba(139,92,246,0.12)',  group: 'in_arbeit' },
  QUALIFIED:         { label: 'Qualifiziert',       kurz: 'Qualifiz.',  color: '#0EA5E9', bgColor: 'rgba(14,165,233,0.12)',  group: 'in_arbeit' },
  ACTIVE:            { label: 'Aktiv',              kurz: 'Aktiv',      color: '#10B981', bgColor: 'rgba(16,185,129,0.12)',  group: 'in_arbeit' },
  TALENT_POOL:       { label: 'Talentpool',         kurz: 'Pool',       color: '#64748B', bgColor: 'rgba(100,116,139,0.12)', group: 'ruhend' },
  INACTIVE:          { label: 'Inaktiv',            kurz: 'Inaktiv',    color: '#6B7280', bgColor: 'rgba(107,114,128,0.12)', group: 'ruhend' },
  DO_NOT_CONTACT:    { label: 'Nicht kontaktieren', kurz: 'Sperre',     color: '#EF4444', bgColor: 'rgba(239,68,68,0.12)',   group: 'ruhend' },
};

export const CANDIDATE_STAGE_ORDER: CandidateStage[] = [
  'NEW', 'IN_PROGRESS', 'CONTACT_ATTEMPTED', 'CONTACTED',
  'QUALIFIED', 'ACTIVE', 'TALENT_POOL', 'INACTIVE', 'DO_NOT_CONTACT',
];

export type CandidatePriority = 'HIGH' | 'NORMAL' | 'LOW';

export const CANDIDATE_PRIORITY_CONFIG: Record<CandidatePriority, { label: string; color: string }> = {
  HIGH:   { label: 'Hoch',    color: '#F59E0B' },
  NORMAL: { label: 'Normal',  color: '#6B7280' },
  LOW:    { label: 'Niedrig', color: '#9CA3AF' },
};

export const STAGE_GROUP_LABEL: Record<StageGroup, string> = {
  unbearbeitet: 'Unbearbeitet',
  in_arbeit: 'In Arbeit',
  ruhend: 'Ruhend',
};

function istStufe(wert: unknown): wert is CandidateStage {
  return typeof wert === 'string' && wert in CANDIDATE_STAGE_CONFIG;
}

export function stageInfo(stage?: string | null): StageInfo {
  if (istStufe(stage)) return CANDIDATE_STAGE_CONFIG[stage];
  return CANDIDATE_STAGE_CONFIG.NEW;
}

export function stageLabel(stage?: string | null, kurz = false): string {
  if (!istStufe(stage)) return '—';
  return kurz ? CANDIDATE_STAGE_CONFIG[stage].kurz : CANDIDATE_STAGE_CONFIG[stage].label;
}

export function stageGroup(stage?: string | null): StageGroup {
  return stageInfo(stage).group;
}

export function istInArbeit(stage?: string | null): boolean {
  return stageGroup(stage) === 'in_arbeit';
}
